import { FeatureFlagsEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { hasEntityAccess, managesConfigArea, managesPermissions, type AuthzCheck } from './authzChecks'
import { all, always, any, ldFlag, omneanOnly, orgFlag, type AvailabilityRule } from './availability'
import {
  adminsOrAssigneeRoles,
  everyRole,
  everyRoleExceptSuppliers,
  nonViewerAdmins,
  superAdmin,
  superAdminOrP2pAdmin,
  type RoleAccessRule,
} from './roleAccess'
import type { ConfigArea, PermissionSetEntity } from '@omnea-app/authz'
import type { RouteKey } from '@omnea-app/fe-routing/routes/routeKeys'

/** `open` still applies availability first; only `exempt` skips every step. */
export type AccessRule =
  | { kind: 'open' }
  | { kind: 'fga'; check: AuthzCheck }
  | { kind: 'fgaOrRole'; check: AuthzCheck; role: RoleAccessRule }
  | { kind: 'role'; role: RoleAccessRule }

/**
 * Which query parameter distinguishes routes sharing one pattern, and the value that selects this
 * route.
 */
export type RouteSegment = { queryParam: string; value: string }

type RouteAddress = {
  /**
   * The Next.js route pattern, as `router.route` reports it. Hand-maintained strings the compiler
   * cannot check against the pages directory: a pattern matching no real page makes its route
   * unresolvable, and so ungated.
   */
  pattern: string
  segment?: RouteSegment
}

/** What reaching a route requires, shared by the routes that require the same thing. */
type RouteRule =
  | { exempt: true }
  | { exempt?: false; availability: AvailabilityRule; access: AccessRule; deniedRedirectTo?: string }

export type RouteDefinition = RouteAddress & RouteRule

const SETTINGS_FALLBACK_ROUTE = '/settings/notifications'

/**
 * Most settings pages share one page file and are told apart by their mode segment. The exceptions are
 * the ones with a page file of their own: Next routes a concrete path ahead of the optional catch-all,
 * so a mode segment for those would match nothing and leave the route unresolvable — hence ungated.
 */
const SETTINGS_PATTERN = '/settings/[[...mode]]'

const modeSegment = (value: string): RouteSegment => ({ queryParam: 'mode', value })

/**
 * What a settings route requires: the configuration area it belongs to, answered by the service where
 * it can and by the caller's role otherwise.
 *
 * TODO(Permissions): the service arm deliberately omits the role dimension, so a configuration-area
 * grant alone admits a non-admin. Their writes will still fail — the backend derives a caller's
 * permission role from session claims alone, so no grant lifts them to `admin`, and most settings
 * endpoints exclude the `user` role. Those endpoints MUST authorize on the service before this is
 * rolled out beyond `FGA_POC`.
 */
const configGated = (area: ConfigArea, role: RoleAccessRule = superAdmin): AccessRule => ({
  kind: 'fgaOrRole',
  check: managesConfigArea(area),
  role,
})

/**
 * `FGA_POC` is deliberately absent from `availability`: it selects which permissions system answers
 * rather than conferring an entitlement, so with it off these routes must redirect rather than report
 * themselves non-existent.
 */
const PERMISSIONS_ROUTE = {
  availability: always(),
  access: { kind: 'fga', check: managesPermissions },
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteRule

const REPORTING_ROUTE = {
  availability: always(),
  access: { kind: 'role', role: nonViewerAdmins },
  deniedRedirectTo: '/',
} as const satisfies RouteRule

/**
 * Each of these is shared by a landing route and the detail pages beneath it, which must answer alike:
 * a nested page left on its own rule stays reachable under terms its parent has stopped allowing.
 */
const AI_BULK_EXTRACTION_ROUTE = {
  availability: ldFlag('RELEASE_AI_BULK_EXTRACTION'),
  access: configGated('organisation_settings'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteRule

const DOCUMENT_STORAGE_ROUTE = {
  availability: orgFlag(FeatureFlagsEnum.ENABLE_DOCUMENT_EXTRACTION),
  access: configGated('document_review'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteRule

const ENVIRONMENT_SYNC_ROUTE = {
  availability: ldFlag('RELEASE_CONFIG_SYNC'),
  access: { kind: 'fgaOrRole', check: managesConfigArea('workflows_and_forms'), role: superAdmin },
  deniedRedirectTo: '/',
} as const satisfies RouteRule

const INTEGRATIONS_ROUTE = {
  availability: always(),
  access: configGated('it'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteRule

const PUBLIC_API_ROUTE = {
  availability: all([orgFlag(FeatureFlagsEnum.PUBLIC_API_UI), orgFlag(FeatureFlagsEnum.PUBLIC_API)]),
  access: configGated('it'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteRule

const INTEGRATION_BUILDER_ROUTE = {
  availability: ldFlag('RELEASE_INTEGRATION_BUILDER_V2'),
  access: configGated('it'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteRule

/** Shared with the `/workflows` routes, which mount the same pages outside the settings tree. */
const WORKFLOW_EDITOR_ROUTE = {
  availability: always(),
  access: configGated('workflows_and_forms', superAdminOrP2pAdmin),
  deniedRedirectTo: '/',
} as const satisfies RouteRule

/** Keyed by entity so a new permission-set family cannot ship with its list route ungated. */
const ENTITY_LIST_ROUTES: Readonly<Record<PermissionSetEntity, RouteRule>> = {
  request: {
    availability: always(),
    access: { kind: 'fgaOrRole', check: hasEntityAccess('request'), role: everyRoleExceptSuppliers },
  },
  supplier: {
    availability: always(),
    access: { kind: 'fgaOrRole', check: hasEntityAccess('supplier'), role: adminsOrAssigneeRoles },
  },
  agreement: {
    availability: orgFlag(FeatureFlagsEnum.CREATE_AGREEMENT),
    access: { kind: 'fgaOrRole', check: hasEntityAccess('agreement'), role: everyRole },
  },
}

export const ROUTE_REGISTRY: Readonly<Record<RouteKey, RouteDefinition>> = {
  requestsList: { ...ENTITY_LIST_ROUTES.request, pattern: '/requests/[[...mode]]' },
  suppliersList: { ...ENTITY_LIST_ROUTES.supplier, pattern: '/suppliers/[[...mode]]' },
  agreementsList: { ...ENTITY_LIST_ROUTES.agreement, pattern: '/agreements/[[...mode]]' },
  // Insights and its legacy `/reporting` aliases follow the side menu, which has hidden them from
  // view-only admins throughout.
  insights: { ...REPORTING_ROUTE, pattern: '/insights/[[...path]]' },
  reporting: { ...REPORTING_ROUTE, pattern: '/reporting/[[...path]]' },
  reportingDataDestinations: {
    pattern: '/reporting/data-destinations',
    availability: ldFlag('RELEASE_REPORTING_CLOUD_DELIVERY_UI_V2'),
    access: { kind: 'role', role: superAdmin },
    deniedRedirectTo: '/',
  },
  workflowsList: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/workflows' },
  workflowAuditLog: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/workflows/edit/[id]/audit-log' },
  workflowDraftEdit: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/workflows/edit/[id]/drafts/[draftId]' },
  workflowEdit: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/workflows/edit/[id]' },
  settingsPermissions: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions' },
  settingsPermissionsConfigAccess: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions/config-access' },
  settingsPermissionSetsV2: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions/permission-sets-v2' },
  settingsPermissionRolesV2: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions/roles-v2' },
  settingsPermissionsSupplierAccess: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions/supplier-access' },
  settingsNotifications: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('notifications'),
    availability: always(),
    access: { kind: 'open' },
  },
  // Both follow the side menu rather than the page: reaching a settings route requires the config area
  // its menu section requires.
  settingsAgentLibrary: {
    pattern: '/settings/agent-library',
    availability: ldFlag('RELEASE_AGENT_MARKETPLACE_V1'),
    access: { kind: 'fgaOrRole', check: managesConfigArea('workflows_and_forms'), role: superAdmin },
    deniedRedirectTo: '/',
  },
  settingsEnvironmentSync: { ...ENVIRONMENT_SYNC_ROUTE, pattern: '/settings/environment-sync' },
  settingsEnvironmentSyncJob: {
    ...ENVIRONMENT_SYNC_ROUTE,
    pattern: '/settings/environment-sync/[workflowId]/[jobId]',
  },
  settingsAdmin: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('admin'),
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigration: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationConfigureMappings: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-configure-mappings'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationMigrateEngagements: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-migrate-engagements'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationMigrateInFlight: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-migrate-in-flight'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationMigrateRenewals: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-migrate-renewals'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationVerifyInFlight: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-verify-in-flight'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAi: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('ai'),
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAuctions: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('auctions'),
    availability: orgFlag(FeatureFlagsEnum.AUCTIONS),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAiBulkExtraction: {
    ...AI_BULK_EXTRACTION_ROUTE,
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('ai-bulk-extraction'),
  },
  settingsAiBulkExtractionJob: { ...AI_BULK_EXTRACTION_ROUTE, pattern: '/settings/ai-bulk-extraction/[jobId]' },
  settingsAiBulkExtractionJobReview: {
    ...AI_BULK_EXTRACTION_ROUTE,
    pattern: '/settings/ai-bulk-extraction/[jobId]/review',
  },
  settingsApi: { ...PUBLIC_API_ROUTE, pattern: SETTINGS_PATTERN, segment: modeSegment('api') },
  settingsApiOAuthApp: { ...PUBLIC_API_ROUTE, pattern: '/settings/api/oauth/[integrationId]' },
  settingsApiOAuthEntities: { ...PUBLIC_API_ROUTE, pattern: '/settings/api/oauth/[integrationId]/entities' },
  settingsApiOAuthEntityFields: {
    ...PUBLIC_API_ROUTE,
    pattern: '/settings/api/oauth/[integrationId]/fields/[entityType]',
  },
  settingsAuditLog: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('audit-log'),
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  // Reached only from a card on the Import page, so it answers to the area that governs Import.
  settingsBulkDocumentsUpload: {
    pattern: '/settings/bulk-documents-upload',
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsExternalArchive: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('external-archive'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAutoFlaggedCategories: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('auto-flagged-categories'),
    availability: orgFlag(FeatureFlagsEnum.TPRM),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsCatalogues: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('catalogues'),
    availability: any([
      orgFlag(FeatureFlagsEnum.PRODUCTS_AND_SERVICES_CATALOGUE),
      orgFlag(FeatureFlagsEnum.PUNCHOUT_CATALOGUES),
      orgFlag(FeatureFlagsEnum.HOSTED_CATALOGUES),
    ]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsDynamicAssignees: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('dynamic-assignees'),
    availability: always(),
    access: configGated('dynamic_assignees'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsCoreData: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('core-data'),
    availability: always(),
    access: configGated('core_data'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsDefaultNotifications: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('default-notifications'),
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsDocumentStorage: { ...DOCUMENT_STORAGE_ROUTE, pattern: '/settings/document-storage' },
  settingsDocumentStorageType: { ...DOCUMENT_STORAGE_ROUTE, pattern: '/settings/document-storage/types/[id]' },
  settingsInternalFeatureFlags: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('__feature-flags'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsForms: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('forms'),
    availability: always(),
    access: configGated('workflows_and_forms', superAdminOrP2pAdmin),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsImport: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('import'),
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsIntegrations: { ...INTEGRATIONS_ROUTE, pattern: SETTINGS_PATTERN, segment: modeSegment('integrations') },
  settingsIntegrationEntities: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/entities',
  },
  settingsIntegrationEntityFields: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/fields/[entityType]',
  },
  settingsIntegrationFilters: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/filters',
  },
  settingsIntegrationPermissions: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/permissions',
  },
  settingsIntegrationProcedures: {
    ...INTEGRATION_BUILDER_ROUTE,
    pattern: '/settings/integrations/[integrationId]/procedures',
  },
  settingsIntegrationProcedureTemplate: {
    ...INTEGRATION_BUILDER_ROUTE,
    pattern: '/settings/integrations/[integrationId]/procedures/[templateId]',
  },
  settingsIntegrationVariables: {
    ...INTEGRATION_BUILDER_ROUTE,
    pattern: '/settings/integrations/[integrationId]/variables',
  },
  settingsLocalisation: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('localisation'),
    availability: orgFlag(FeatureFlagsEnum.CONFIG_TRANSLATIONS),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsPolicies: {
    pattern: '/settings/policies',
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  // A policy is shared by link, so its own page stays reachable by anyone the documents service will
  // answer for; the store that lists every policy does not.
  settingsPolicy: { pattern: '/settings/policies/[id]', availability: always(), access: { kind: 'open' } },
  settingsPreferences: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('preferences'),
    availability: always(),
    access: { kind: 'open' },
  },
  settingsProductCatalogue: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('product-catalogue'),
    availability: orgFlag(FeatureFlagsEnum.PRODUCTS_AND_SERVICES_CATALOGUE),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsPunchoutCatalogues: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('punchout-catalogues'),
    availability: all([omneanOnly(), orgFlag(FeatureFlagsEnum.PUNCHOUT_CATALOGUES), ldFlag('SHOW_PUNCHOUT_CATALOGUES')]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsHostedCatalogues: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('hosted-catalogues'),
    availability: orgFlag(FeatureFlagsEnum.HOSTED_CATALOGUES),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsCategories: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('categories'),
    availability: any([
      orgFlag(FeatureFlagsEnum.PRODUCTS_AND_SERVICES_CATALOGUE),
      orgFlag(FeatureFlagsEnum.SUPPLIER_CATEGORISATION),
    ]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsRisks: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('risks'),
    availability: orgFlag(FeatureFlagsEnum.TPRM),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsRiskManagement: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('risk-management'),
    availability: orgFlag(FeatureFlagsEnum.TPRM),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsRiskAlerts: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('risk-alerts'),
    availability: orgFlag(FeatureFlagsEnum.TPRM_CONTINUOUS_MONITORING),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsSubprocessorSettings: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('subprocessor-settings'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), orgFlag(FeatureFlagsEnum.SUPPLIER_SUBPROCESSORS)]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsArtefactCatalogue: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('artefact-catalogue'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), ldFlag('RELEASE_ARTEFACTS_AGENT')]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsArtefactGroups: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('artefact-groups'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), ldFlag('RELEASE_ARTEFACTS_AGENT')]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsArtefactsLibrary: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('artefacts-library'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), ldFlag('RELEASE_ARTEFACTS_AGENT')]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsGoodsReceipting: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('goods-receipting'),
    availability: orgFlag(FeatureFlagsEnum.GOODS_RECEIPTING),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsInvoiceProcessing: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('invoice-processing'),
    availability: orgFlag(FeatureFlagsEnum.INVOICE_PROCESSING),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsSecurity: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('security'),
    availability: always(),
    access: configGated('it'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsSourcing: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('sourcing'),
    availability: orgFlag(FeatureFlagsEnum.RFX),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsSourcingMemory: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('sourcing-memory'),
    availability: all([omneanOnly(), orgFlag(FeatureFlagsEnum.RFX)]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsUserProperties: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('user-properties'),
    availability: always(),
    access: configGated('it'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsUsersAndGroups: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('users-and-groups'),
    availability: always(),
    access: configGated('users_and_groups'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsWorkflows: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('workflows'),
    availability: always(),
    access: configGated('workflows_and_forms', superAdminOrP2pAdmin),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsWorkflowDraftEdit: {
    ...WORKFLOW_EDITOR_ROUTE,
    pattern: '/settings/workflows/edit/[id]/drafts/[draftId]',
  },
  settingsWorkflowEdit: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/settings/workflows/edit/[id]' },
}

// `Object.entries` widens the key to `string`; `ROUTE_REGISTRY` is `Record<RouteKey, …>`, so every key is a `RouteKey`.
export const ROUTE_REGISTRY_ENTRIES = Object.entries(ROUTE_REGISTRY) as readonly [RouteKey, RouteDefinition][]
